import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, finalize, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../auth-service/auth-service.service';
import { Art57Section } from '../../ui/Art57Section/Art57Section';
import { BaseNode } from '../../ui/Node/BaseNode';
import { TrackerServiceComponent } from '../tracker-service/tracker.service';
import { AssessmentResultSummaryInfo } from '../../ui/Data/Assessment/AssessmentResultInfo';
import { LookupGetService } from '../lookup-service/lookup-get.service';
import { ConfirmationService } from 'primeng/api';
@Injectable({
  providedIn: 'root'
})
export class MedicinalproductsService {

  public loading = new BehaviorSubject<boolean>(false);

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private injector: Injector) { }

  getAll(node: BaseNode, entity: string, criteria: string, fields: string): Observable<{}> {
    return this.get(node, entity, criteria, 0, 2000, 2000, false, fields);
  }

  filter(node: BaseNode, entity: string, criteria: string, pageNumber: number, or: boolean, fields: string): Observable<{}> {
    return this.get(node, entity, criteria, pageNumber, node.selectedPageSize, node.selectedPageSize, or, fields);
  }

  get(node: BaseNode, entity: string, criteria: string, pageNumber: number, pageSize: number, fetchSize: number, or: boolean, fields: string): Observable<{}> {

    this.loading.next(true);
    let headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken(),
    });

    let params = new HttpParams().set('c', criteria);
    params = params.append('p', pageNumber);
    params = params.append('s', pageSize);
    params = params.append('f', fetchSize);
    params = params.append('or', or);
    params = params.append('fi', fields);
    params = params.append('o', node.offSet);

    return this.httpClient
      .get(`${environment.apiUrl}/api/medprod/search/${entity}`, {
        headers: headers, params: params
      })
      .pipe(
        map((resp) => {
          const result = resp as [];
          this.loading.next(false);
          return { node, result };
        })
      );
  }

  filterToExcel(entity: string, criteria: string, pageNumber: number, or: boolean, isExport: boolean, fields: any): Observable<HttpResponse<Blob>> {

    const trackerKey = `filterToExcel-${entity}-${criteria}`;
    if (!this.injector.get(TrackerServiceComponent).start(trackerKey)) {
      return EMPTY;
    }
    let headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken(),
    });

    let params = new HttpParams().set('c', criteria);
    params = params.append('p', pageNumber);
    if (!isExport) {
      params = params.append('s', 0);
      params = params.append('f', 1000000);
    }
    params = params.append('or', or);
    params = params.append('fd', fields);

    return this.httpClient.get(`${environment.apiUrl}/api/medprod/search/excel/${entity}`, { headers: headers, params: params, responseType: 'blob', observe: 'response' })
          .pipe(
            finalize(() => this.injector.get(TrackerServiceComponent).stop(trackerKey))
            );
  }

  exportResultsToExcel(entity: string, data: any, fieldsToRetrieve: string): Observable<HttpResponse<Blob>> {

    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken()
    });

    const formData = new FormData();
    formData.append("data", new Blob(
      [JSON.stringify(data)],
      { type: "application/json" }
    ));
    formData.append("fields", new Blob(
      [fieldsToRetrieve],
      { type: "application/json" }
    ));
    return this.httpClient.post(`${environment.apiUrl}/api/medprod/export/excel/${entity}`, formData, { headers: headers, responseType: 'blob', observe: 'response' });
  }

  compare(entity: string, id: any,compareTo: string) {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken()
    });

    return this.httpClient
      .get(`${environment.apiUrl}/api/medprod/compare/${entity}/${id}`, {
        headers: headers,
        params: {
          compareTo
        }

      })
      .pipe(
        map((resp) => {
          const result = resp as [];
          this.loading.next(false);
          return result;
        })
      );

  }


  exportResults(entity: string, data: [], onDemandEntities: any, multiple: boolean, format: string): Observable<HttpResponse<Blob>> {

    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken()
    });

    const formData = new FormData();
    formData.append("entityIds", new Blob(
      [JSON.stringify(data)],
      { type: "application/json" }
    ));

    formData.append("onDemandEntities", new Blob(
      [JSON.stringify(onDemandEntities)],
      { type: "application/json" }
    ));

    return this.httpClient.post(`${environment.apiUrl}/api/medprod/export/${format === 'xml' ? (multiple ? 'xmls' : 'xml') : (multiple ? 'rtfs' : 'rtf')}/${entity}`, formData, { headers: headers, responseType: 'blob', observe: 'response' });
  }

  loadEntity(entityName: string, code: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken()
    });

    this.injector.get(MedicinalproductsService).loading.next(true);

    return this.httpClient
      .get(`${environment.apiUrl}/api/medprod/${entityName}/${code}`, {
        headers: headers
      })
      .pipe(
        map((resp) => {
          const result = resp;
          this.loading.next(false);
          return result;
        })
      );
  }

  loadEntities(entityName: string, entityIds: number[]): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken()
    });

    let params = new HttpParams();

    entityIds.forEach((id) => {
      params = params.append('entityIds', id.toString());
    });

    this.injector.get(MedicinalproductsService).loading.next(true);

    return this.httpClient
      .get(`${environment.apiUrl}/api/medprod/${entityName}`, {
        headers: headers,
        params: params
      })
      .pipe(
        map((resp) => {
          const result = resp;
          this.loading.next(false);
          return result;
        })
      );
  }

  exportResultToXml(entity: string, data: []) {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken()
    });

    const formData = new FormData();
    formData.append("entityIds", new Blob(
      [JSON.stringify(data.map(t => t["id"]))],
      { type: "application/json" }
    ));

    return this.httpClient.post(`${environment.apiUrl}/api/medprod/export/xml/${entity}`, formData, { headers: headers, responseType: 'blob' });
  }


  getAttachmentDocument(id: string): Observable<any> {

    this.injector!.get(Art57Section).showTooltipMessage({ severity: 'success', detail: `Downloading Attachment Document`, life: environment.successElapsedTime });
    this.loading.next(true);
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken()
    });

    const params = new HttpParams().set('id', id);
    return this.httpClient
      .post(`${environment.apiUrl}/api/medprod/attachmentDocument`, null,
        {
          params, headers: headers, responseType: 'blob', observe: 'response'
        })
  }

  assess(entityName: string, assessType: string, entityIds: number[]): Observable<AssessmentResultSummaryInfo> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken()
    });

    const formData = new FormData();
    formData.append("entityIds", new Blob(
      [JSON.stringify(entityIds)],
      { type: "application/json" }
    ));

    return this.httpClient.post<AssessmentResultSummaryInfo>(
      `${environment.apiUrl}/api/medprod/assess/${assessType}/${entityName}`,
      formData,
      { headers }
    );
  }

  getXML(id:string, endpoint:string):Promise<string> {
    return new Promise((resolve, reject) => {
      let lookupName: string = endpoint;
        this.injector.get(LookupGetService).get(lookupName, id).subscribe({
          next: data => {
            resolve(data.name);
          },
          error: () => {
            reject('');
          }
        });
      });
  }
  
openLink(endpoint: string, messageId: string, linkLabel: string): void {

      this.getXML(messageId,endpoint).then(data => {
        const link = document.createElement('a');
        let url: string;
        url = URL.createObjectURL(new Blob([data], { type: 'text/xml' }));
        link.href = url;
        link.download = linkLabel + ".xml";

        this.confirmationService.confirm({
          header: 'Message information',
          message: "Click on the link below to review the XML message.<br><br><a href='" + link.href + "' target='_blank'>" + link.download + "</a>",
          icon: 'pi pi-question-circle',
          acceptLabel: 'Download',
          rejectLabel: 'Close',
          accept: () => {
            link.click();
            URL.revokeObjectURL(url);
          },
          reject: () => {
            URL.revokeObjectURL(url);
          }
        });
      });

  }
}